"use client";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "next-view-transitions";
import React from "react";

type Tone = "green" | "blue" | "amber" | "rose";

type Stream = {
  transcript: string;
  outcome: string;
  actions: string[];
  outcomeTone: Tone;
  topPercent: number;
  duration: number;
  delay: number;
};

type StreamConfig = {
  key: "s1" | "s2" | "s3" | "s4";
  actionKeys: string[];
  outcomeTone: Tone;
  topPercent: number;
  duration: number;
  delay: number;
};

const STREAM_CONFIGS: StreamConfig[] = [
  {
    key: "s1",
    actionKeys: ["a1", "a2"],
    outcomeTone: "green",
    topPercent: 12,
    duration: 13,
    delay: 0,
  },
  {
    key: "s2",
    actionKeys: ["a1", "a2"],
    outcomeTone: "blue",
    topPercent: 36,
    duration: 15,
    delay: 3.5,
  },
  {
    key: "s3",
    actionKeys: ["a1"],
    outcomeTone: "amber",
    topPercent: 60,
    duration: 14,
    delay: 7,
  },
  {
    key: "s4",
    actionKeys: ["a1", "a2"],
    outcomeTone: "rose",
    topPercent: 84,
    duration: 12,
    delay: 10,
  },
];

const OUTCOME_CLASS: Record<Tone, string> = {
  green: "text-emerald-400 bg-emerald-950/60 border-emerald-900",
  blue: "text-blue-400 bg-blue-950/60 border-blue-900",
  amber: "text-amber-400 bg-amber-950/60 border-amber-900",
  rose: "text-rose-400 bg-rose-950/60 border-rose-900",
};

type Copy = {
  autodoc_label: string;
  autodoc_sublabel: string;
  card_label: string;
  card_footer: string;
};

export const AfterburnSection = () => {
  const t = useTranslations("afterburn");

  const streams: Stream[] = STREAM_CONFIGS.map((cfg) => ({
    transcript: t(`${cfg.key}_transcript`),
    outcome: t(`${cfg.key}_outcome`),
    actions: cfg.actionKeys.map((a) => t(`${cfg.key}_${a}`)),
    outcomeTone: cfg.outcomeTone,
    topPercent: cfg.topPercent,
    duration: cfg.duration,
    delay: cfg.delay,
  }));

  const autodocCopy: Copy = {
    autodoc_label: t("autodoc_label"),
    autodoc_sublabel: t("autodoc_sublabel"),
    card_label: t("card_label"),
    card_footer: t("card_footer"),
  };

  return (
    <section
      id="afterburn"
      className="dark relative w-full overflow-hidden bg-neutral-950 scroll-mt-24"
    >
      <SectionBackground />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-36">
        <BrandReveal />
        <div className="mt-12 lg:mt-20">
          <DialerFlowScene streams={streams} autodocCopy={autodocCopy} />
        </div>
        <div className="mt-14 lg:mt-24">
          <FeatureSplit />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Background — dark band + radial red glow + ember particles
// ============================================================================

const SectionBackground = () => {
  return (
    <>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_40%,rgba(239,68,68,0.18),transparent_65%)]" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent" />
      <EmberParticles />
    </>
  );
};

const EmberParticles = () => {
  const seeds = React.useMemo(
    () =>
      Array.from({ length: 22 }).map((_, i) => ({
        left: (i * 41) % 100,
        size: 1 + (i % 3) * 0.5,
        duration: 7 + (i % 5) * 1.3,
        delay: (i * 0.37) % 8,
      })),
    []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {seeds.map((s, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-red-400"
          style={{
            left: `${s.left}%`,
            bottom: 0,
            width: `${s.size}px`,
            height: `${s.size}px`,
            boxShadow: "0 0 6px 1px rgba(239,68,68,0.7)",
          }}
          initial={{ y: 0, opacity: 0 }}
          animate={{
            y: [-20, -600 - (i % 4) * 60],
            opacity: [0, 0.9, 0.9, 0],
            x: [0, (i % 2 === 0 ? 1 : -1) * (10 + (i % 3) * 6)],
          }}
          transition={{
            repeat: Infinity,
            duration: s.duration,
            delay: s.delay,
            ease: "linear",
            times: [0, 0.15, 0.75, 1],
          }}
        />
      ))}
    </div>
  );
};

// ============================================================================
// Block 1 — Brand Reveal
// ============================================================================

const BrandReveal = () => {
  const t = useTranslations("afterburn");
  return (
    <div className="flex flex-col items-center text-center">
      <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-red-300">
        <FlameIcon className="h-3 w-3 text-red-400" />
        <span>{t("eyebrow")}</span>
      </div>

      <h2 className="mt-6 font-black leading-none tracking-tight text-5xl sm:text-6xl md:text-8xl lg:text-[9rem]">
        <span className="bg-clip-text text-transparent bg-gradient-to-br from-red-300 via-red-500 to-red-600 drop-shadow-[0_0_40px_rgba(239,68,68,0.35)]">
          AFTERBURN
        </span>
      </h2>

      <p className="mt-6 text-2xl md:text-4xl font-semibold text-white max-w-3xl leading-tight">
        {t("tagline")}
      </p>

      <p className="mt-5 text-sm md:text-base text-neutral-400 max-w-2xl leading-relaxed">
        {t("lead")}
      </p>
    </div>
  );
};

// ============================================================================
// Block 2 — Live Demo Scene (Dialer ⊕ Afterburn)
// ============================================================================

const DialerFlowScene = ({
  streams,
  autodocCopy,
}: {
  streams: Stream[];
  autodocCopy: Copy;
}) => {
  return (
    <div className="relative rounded-2xl border border-neutral-800 bg-neutral-950 shadow-[0_0_60px_rgba(239,68,68,0.15)] overflow-hidden">
      <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-red-500/10 pointer-events-none" />
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[460px]">
        <DialerPane />
        <AutoDocPane streams={streams} copy={autodocCopy} />
      </div>
    </div>
  );
};

const DialerPane = () => {
  const t = useTranslations("afterburn");
  const queue = [
    { name: "Anna Schmidt", company: t("queue_1_role"), status: "ready" as const },
    { name: "Tom Becker", company: t("queue_2_role"), status: "ready" as const },
    { name: "Lisa Roth", company: t("queue_3_role"), status: "queued" as const },
    { name: "Ahmed Faris", company: t("queue_4_role"), status: "queued" as const },
  ];
  return (
    <div className="lg:col-span-5 border-b lg:border-b-0 lg:border-r border-neutral-800 bg-neutral-900/40 p-4 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[10px] font-semibold tracking-wider text-emerald-400 uppercase">
            {t("live_label")}
          </span>
        </div>
        <span className="text-[10px] text-neutral-500">{t("queue_label")}</span>
      </div>

      <ActiveCallCard />

      <div className="mt-4 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium mb-2">
          {t("next_in_queue")}
        </div>
        <ul className="space-y-1.5">
          {queue.map((q, i) => (
            <QueueItem key={i} name={q.name} company={q.company} status={q.status} />
          ))}
        </ul>
      </div>

      <div className="mt-4 text-[10px] text-neutral-500 leading-relaxed">
        {t("agent_focus_label")}{" "}
        <span className="text-neutral-300 font-medium">{t("agent_focus_value")}</span>
      </div>
    </div>
  );
};

const ActiveCallCard = () => {
  const t = useTranslations("afterburn");
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900 p-3 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider mb-1">
            {t("in_call")}
          </div>
          <div className="text-sm font-semibold text-neutral-100">Marcus Weber</div>
          <div className="text-[11px] text-neutral-500">{t("active_role")}</div>
        </div>
        <CallTimer />
      </div>

      <div className="mt-3 flex items-center gap-1.5">
        <DialerButton label={t("btn_mute")} />
        <DialerButton label={t("btn_hangup")} danger />
        <DialerButton label={t("btn_rec")} />
      </div>
    </div>
  );
};

const CallTimer = () => {
  const [seconds, setSeconds] = React.useState(154);
  React.useEffect(() => {
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const mm = Math.floor(seconds / 60).toString().padStart(2, "0");
  const ss = (seconds % 60).toString().padStart(2, "0");
  return (
    <span className="text-xs font-mono tabular-nums text-neutral-400">
      {mm}:{ss}
    </span>
  );
};

const DialerButton = ({ label, danger }: { label: string; danger?: boolean }) => (
  <span
    className={cn(
      "text-[10px] font-medium px-2 py-1 rounded-md border",
      danger
        ? "text-rose-400 border-rose-900 bg-rose-950/60"
        : "text-neutral-400 border-neutral-800 bg-neutral-900"
    )}
  >
    {label}
  </span>
);

const QueueItem = ({
  name,
  company,
  status,
}: {
  name: string;
  company: string;
  status: "ready" | "queued";
}) => {
  const t = useTranslations("afterburn");
  return (
    <li className="flex items-center justify-between text-[11px] text-neutral-400 bg-neutral-900/60 rounded-md border border-neutral-800 px-2 py-1.5">
      <div className="min-w-0">
        <div className="font-medium text-neutral-300 truncate">{name}</div>
        <div className="text-[10px] text-neutral-500 truncate">{company}</div>
      </div>
      <span
        className={cn(
          "text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded",
          status === "ready"
            ? "text-emerald-400 bg-emerald-950/60"
            : "text-neutral-500 bg-neutral-800"
        )}
      >
        {status === "ready" ? t("status_ready") : t("status_queued")}
      </span>
    </li>
  );
};

const AutoDocPane = ({ streams, copy }: { streams: Stream[]; copy: Copy }) => {
  return (
    <div className="lg:col-span-7 relative overflow-hidden p-4 bg-gradient-to-br from-neutral-950 via-neutral-950 to-red-950/20">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <FlameIcon className="h-3.5 w-3.5 text-red-500 animate-pulse" />
          <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
            {copy.autodoc_label}
          </span>
        </div>
        <span className="text-[10px] text-neutral-500">{copy.autodoc_sublabel}</span>
      </div>

      <div className="relative h-[400px]">
        <IgnitionSeam />
        {streams.map((s, i) => (
          <StreamRow key={i} stream={s} copy={copy} />
        ))}
      </div>
    </div>
  );
};

const IgnitionSeam = () => (
  <>
    <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[2px] bg-gradient-to-b from-transparent via-red-500/50 to-transparent" />
    <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[14px] bg-gradient-to-b from-transparent via-red-500/10 to-transparent blur-md" />
    <motion.div
      className="absolute left-1/2 -translate-x-1/2 w-[3px] h-24 rounded-full bg-gradient-to-b from-transparent via-red-400 to-transparent"
      style={{ boxShadow: "0 0 14px 3px rgba(239,68,68,0.7)" }}
      initial={{ top: "-15%" }}
      animate={{ top: ["-15%", "115%"] }}
      transition={{ repeat: Infinity, duration: 5, ease: "linear" }}
    />
  </>
);

const StreamRow = ({ stream, copy }: { stream: Stream; copy: Copy }) => {
  const common = {
    repeat: Infinity,
    duration: stream.duration,
    delay: stream.delay,
    ease: "linear" as const,
  };
  return (
    <div
      className="absolute inset-x-0 h-0"
      style={{ top: `${stream.topPercent}%` }}
    >
      <motion.div
        className="absolute top-0 -translate-y-1/2"
        initial={{ left: "0%", opacity: 0 }}
        animate={{
          left: ["0%", "8%", "30%", "38%"],
          opacity: [0, 1, 1, 0],
        }}
        transition={{ ...common, times: [0, 0.18, 0.38, 0.48] }}
      >
        <div className="inline-flex items-center gap-2 bg-neutral-900 text-neutral-300 text-xs font-mono px-2.5 py-1.5 rounded-lg border border-neutral-800 shadow-sm whitespace-nowrap">
          <WaveformIcon />
          <span>&ldquo;{stream.transcript}&rdquo;</span>
        </div>
      </motion.div>

      <motion.div
        className="absolute top-0 -translate-y-1/2 right-0"
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{
          opacity: [0, 1, 1, 0],
          scale: [0.92, 1, 1, 1],
        }}
        transition={{ ...common, times: [0.48, 0.58, 0.9, 1] }}
      >
        <SummaryCard stream={stream} copy={copy} />
      </motion.div>
    </div>
  );
};

const SummaryCard = ({ stream, copy }: { stream: Stream; copy: Copy }) => (
  <div className="bg-neutral-900 border border-neutral-800 rounded-xl shadow-md p-3 w-60 relative">
    <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent" />
    <div className="flex items-center justify-between mb-2">
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-red-400">
        <FlameIcon className="h-3 w-3" />
        {copy.card_label}
      </span>
      <span
        className={cn(
          "text-[10px] font-semibold px-1.5 py-0.5 rounded border",
          OUTCOME_CLASS[stream.outcomeTone]
        )}
      >
        ✓ {stream.outcome}
      </span>
    </div>
    <ul className="space-y-1">
      {stream.actions.map((action, i) => (
        <li
          key={i}
          className="text-[11px] text-neutral-400 flex items-center gap-1.5"
        >
          <span className="text-red-500 text-[13px] leading-none">→</span>
          <span>{action}</span>
        </li>
      ))}
    </ul>
    <div className="mt-2 pt-2 border-t border-neutral-800 text-[9px] text-neutral-500 flex items-center gap-1">
      <span className="h-1 w-1 rounded-full bg-red-500 animate-pulse" />
      {copy.card_footer}
    </div>
  </div>
);

const WaveformIcon = () => (
  <svg
    width="14"
    height="10"
    viewBox="0 0 14 10"
    fill="none"
    className="text-emerald-500 flex-shrink-0"
    aria-hidden
  >
    <rect x="0" y="3" width="1.5" height="4" fill="currentColor" rx="0.5">
      <animate attributeName="height" values="4;7;4" dur="0.9s" repeatCount="indefinite" />
      <animate attributeName="y" values="3;1.5;3" dur="0.9s" repeatCount="indefinite" />
    </rect>
    <rect x="3" y="2" width="1.5" height="6" fill="currentColor" rx="0.5">
      <animate attributeName="height" values="6;3;6" dur="0.7s" repeatCount="indefinite" />
      <animate attributeName="y" values="2;3.5;2" dur="0.7s" repeatCount="indefinite" />
    </rect>
    <rect x="6" y="1" width="1.5" height="8" fill="currentColor" rx="0.5">
      <animate attributeName="height" values="8;4;8" dur="1.1s" repeatCount="indefinite" />
      <animate attributeName="y" values="1;3;1" dur="1.1s" repeatCount="indefinite" />
    </rect>
    <rect x="9" y="2" width="1.5" height="6" fill="currentColor" rx="0.5">
      <animate attributeName="height" values="6;2;6" dur="0.8s" repeatCount="indefinite" />
      <animate attributeName="y" values="2;4;2" dur="0.8s" repeatCount="indefinite" />
    </rect>
    <rect x="12" y="3" width="1.5" height="4" fill="currentColor" rx="0.5">
      <animate attributeName="height" values="4;6;4" dur="1s" repeatCount="indefinite" />
      <animate attributeName="y" values="3;2;3" dur="1s" repeatCount="indefinite" />
    </rect>
  </svg>
);

// ============================================================================
// Block 3 — Feature Split (Call Documentator ⊕ Workflows)
// ============================================================================

const FeatureSplit = () => {
  const t = useTranslations("afterburn");
  const locale = useLocale();

  return (
    <div>
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-red-300">
          <span>{t("split_eyebrow")}</span>
        </div>
        <h3 className="mt-5 text-2xl md:text-3xl lg:text-4xl font-bold text-white tracking-tight">
          {t("split_heading")}
        </h3>
        <p className="mt-4 text-sm md:text-base text-neutral-400 leading-relaxed">
          {t("split_lead")}
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        <FeatureCard
          icon={<IconNotes />}
          title={t("cd_title")}
          subtitle={t("cd_subtitle")}
          bullets={[t("cd_b1"), t("cd_b2"), t("cd_b3")]}
          ctaLabel={t("cd_cta")}
          href={`/${locale}/features/call-documentator`}
        />
        <FeatureCard
          icon={<IconBolt />}
          title={t("wf_title")}
          subtitle={t("wf_subtitle")}
          bullets={[t("wf_b1"), t("wf_b2"), t("wf_b3"), t("wf_b4")]}
          ctaLabel={t("wf_cta")}
          href={`/${locale}/features/workflows`}
        />
      </div>
    </div>
  );
};

const FeatureCard = ({
  icon,
  title,
  subtitle,
  bullets,
  ctaLabel,
  href,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  bullets: string[];
  ctaLabel: string;
  href: string;
}) => (
  <Link
    href={href}
    className="group relative rounded-2xl border border-neutral-800 bg-neutral-900/50 backdrop-blur p-6 md:p-7 overflow-hidden transition-colors hover:border-red-500/40 flex flex-col"
  >
    <div
      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
      style={{
        background:
          "radial-gradient(circle at 30% 0%, rgba(239,68,68,0.14), transparent 65%)",
      }}
    />
    <div className="relative flex-1">
      <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-red-500/10 text-red-400 ring-1 ring-red-500/30">
        {icon}
      </div>
      <h4 className="mt-4 text-lg md:text-xl font-bold text-white tracking-tight">
        {title}
      </h4>
      <p className="mt-1.5 text-sm text-neutral-400 leading-relaxed">
        {subtitle}
      </p>
      <ul className="mt-5 space-y-2.5">
        {bullets.map((b, i) => (
          <li
            key={i}
            className="flex items-start gap-2.5 text-sm text-neutral-300"
          >
            <span className="mt-1 flex h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
    <div className="relative mt-6 flex items-center gap-1.5 text-sm font-semibold text-red-300 group-hover:text-red-200 transition-colors">
      <span>{ctaLabel}</span>
      <svg
        viewBox="0 0 16 16"
        className="h-4 w-4 transition-transform group-hover:translate-x-1"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M3 8h10M9 4l4 4-4 4" />
      </svg>
    </div>
  </Link>
);

// ============================================================================
// Icons
// ============================================================================

function FlameIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M12 2.5c1.3 3.1 3.8 4.6 4.7 6.9.9 2.3.3 4.7-1.4 6.4-.6.6-1.3 1-2.1 1.2.5-.9.6-2 .2-3-.5-1.2-1.7-1.9-2.9-1.9-.3 1-.9 1.9-1.7 2.5-1.7 1.3-2.3 3.5-1.6 5.4.5 1.4 1.6 2.6 3 3-3.7-.2-7-3.1-7.4-6.9-.4-4.1 2.4-7.7 5.5-10.1C10.3 4.7 11.4 3.6 12 2.5z" />
    </svg>
  );
}

function IconNotes() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M4 5a2 2 0 0 1 2-2h9l5 5v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5Z" />
      <path d="M15 3v4a2 2 0 0 0 2 2h4" />
      <path d="M8 13h8M8 17h5" />
    </svg>
  );
}

function IconBolt() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  );
}
